'use client'

import { useMemo, useState } from "react"
import CopyButton from "@/components/elements/CopyButton"
import { highlight, type TokenKind } from "@/lib/docs/highlight"
import type { CodeTab } from "@/lib/docs/types"

// Theme colours, so the code reads in light and dark alike.
const colors: Record<TokenKind, string> = {
  comment: 'text-muted-foreground',
  string: 'text-success',
  keyword: 'text-purple',
  number: 'text-warning',
  type: 'text-warning',
  name: 'text-blue',
  property: 'text-blue',
}

// One request in several languages, or a single labelled block when there is one entry.
export default function CodeTabs({ tabs, caption }: { tabs: CodeTab[], caption?: React.ReactNode }) {
  const [active, setActive] = useState(0)
  const current = tabs[active] || tabs[0]
  const tokens = useMemo(() => highlight(current.code, current.language), [current.code, current.language])

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <div className="flex items-center justify-between gap-2 border-b bg-muted/40 py-1.5 pl-2 pr-1.5">
        <div className="flex items-center gap-1" role={tabs.length > 1 ? 'tablist' : undefined}>
          {tabs.length > 1 ? tabs.map((tab, index) => (
            <button key={tab.label} type="button" role="tab" aria-selected={index === active} onClick={() => setActive(index)}
              className={`cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${index === active ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}>
              {tab.label}
            </button>
          )) : <span className="px-2 py-1 text-xs font-medium text-muted-foreground">{caption || current.label}</span>}
        </div>
        <CopyButton value={current.code} className="size-7" />
      </div>
      <pre className="scrollbar overflow-x-auto p-4 text-[13px] leading-6">
        <code>{tokens.map((token, index) => (token.kind ? <span key={index} className={colors[token.kind]}>{token.text}</span> : token.text))}</code>
      </pre>
    </div>
  )
}
